import { useCallback, useContext, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Edit,
  Visibility,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import UserContext from "../Contexts/UserContext";
import {
  API_GetAllTimesheets,
  API_CreateTimesheet,
  API_GetCandidateJobs,
  API_GetCandidateIdFromEmail,
  API_DeleteTimesheet,
  API_GetCandidateByID,
} from "../Assets/ApiUrlContainer";
import { default as axios } from "../axiosConfig";
import TimesheetDetails from "./TimesheetDetails";
import { Pagination } from "./Pagination";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TextField } from "@mui/material";

const EmployeeTimesheets = () => {
  const currentUser = useContext(UserContext);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("WeekStartDate");
  const [sortOrder, setSortOrder] = useState("asc");
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedNewWeek, setSelectedNewWeek] = useState("");
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState("none");
  const [jobAvailableWeeks, setJobAvailableWeeks] = useState({});
  const [timesheets, setTimesheets] = useState([]);
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    notSubmitted: true,
    submitted: true,
    approved: true,
  });
  const [openDialog, setOpenDialog] = useState(false);

  const userEmail = currentUser?.user?.Email;

  const convertDateForDayJS = (dateToConvert) => {
    let date = new Date(dateToConvert);
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(-2)}-${("0" + date.getUTCDate()).slice(-2)}`;
  }

  const fetchAllTimesheets = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(API_GetAllTimesheets, {
        params: { Email: userEmail, Role: currentUser.user.Role, CandidateId: currentUser.user.CandidateId },
      });

      const allTimesheets = response.data.timesheets || [];
      setTimesheets(response.data.timesheets);

      // find which timesheets exist for each week for each job
      const existingWeeksByJob = allTimesheets.reduce((acc, ts) => {
        if (!acc[ts.JobId]) acc[ts.JobId] = new Set();
        acc[ts.JobId].add(dayjs(convertDateForDayJS(ts.WeekStartDate)).format("YYYY-MM-DD"));
        return acc;
      }, {});

      // Generate all Mondays for the current year
      const calendarStart = dayjs().subtract(1, "year");
      const calendarEnd = dayjs().add(1, "year");

      let mondays = [];
      let currentMonday = calendarStart.day(1); // Move to first Monday

      if (currentMonday.isBefore(calendarStart)) {
        currentMonday = currentMonday.add(7, "days"); // Ensure it's within the year
      }

      while (currentMonday.isBefore(calendarEnd)) {
        mondays.push(currentMonday.format("YYYY-MM-DD"));
        currentMonday = currentMonday.add(7, "days"); // Move to next Monday
      }

      // Generate available weeks per job
      let jobAvailableWeeksMap = {};
      jobs.forEach((job) => {
        // Exclude weeks that already have a timesheet
        const jobWeeks = mondays.filter((week) => !existingWeeksByJob[job.Id]?.has(week));
        jobAvailableWeeksMap[job.Id] = jobWeeks;
      });

      setJobAvailableWeeks(jobAvailableWeeksMap);

      // Set default available weeks if a job is selected
      if (selectedJob && jobAvailableWeeksMap[selectedJob]) {
        setAvailableWeeks(jobAvailableWeeksMap[selectedJob]);
      }

    } catch (error) {
      console.error("Error fetching timesheets:", error);
    } finally {
      setLoading(false);
    }

  }, [userEmail, jobs, selectedJob, currentUser.user.Role]);

  const fetchCandidateInfo = useCallback(async () => {
    try {
      let response = null;

      if (currentUser?.user?.CandidateId) {
        response = await axios.get(`${API_GetCandidateByID}/${currentUser?.user?.CandidateId}`);

      } else {
        response = await axios.get(`${API_GetCandidateIdFromEmail}`, {
          params: { Email: userEmail },
        });
      }

      const candidateId = response.data.Id;
      const res = await axios.get(`${API_GetCandidateJobs}/${candidateId}`);
      const jobsList = res.data;

      setJobs(jobsList);
      if (jobsList.length > 0) {
        setSelectedJob(jobsList[0].Id);
      }
    } catch (error) {
      console.error("Error fetching candidate info:", error);
    }
  }, [userEmail]);

  // Fetch candidate info once when component mounts
  useEffect(() => {
    if (userEmail) {
      fetchCandidateInfo();
    }
  }, [userEmail, fetchCandidateInfo]);

  useEffect(() => {
    if (userEmail && jobs.length > 0) {
      fetchAllTimesheets();
    }
    if (jobs.length > 0 && selectedJob === "none") {
      setSelectedJob(jobs[0].Id);
    }
  }, [userEmail, jobs, fetchAllTimesheets, currentUser.user.role]);

  const handleDeleteTimesheet = async (timesheetId) => {
    try {
      await axios.delete(`${API_DeleteTimesheet}/${timesheetId}`);
      fetchAllTimesheets(); // Refresh the table
    } catch (error) {
      console.error("Failed to delete timesheet:", error);
    }
  };

  const handleJobSelection = (jobId) => {
    setSelectedJob(jobId);

    // Update available weeks based on selected job
    if (jobAvailableWeeks[jobId]) {
      setAvailableWeeks(jobAvailableWeeks[jobId]);
    } else {
      setAvailableWeeks([]);
    }

    // Reset selected week when changing jobs
    setSelectedNewWeek("");
  };

  // Handle new timesheet creation
  const handleCreateTimesheet = async () => {
    if (!selectedNewWeek || !selectedJob) return; // ensure a job + week have been selected
    const jobTitle = jobs.find((job) => job.Id === selectedJob)?.Name || "Unknown";

    try {
      await axios.post(API_CreateTimesheet, {
        Email: userEmail,
        StartDate: selectedNewWeek,
        EndDate: dayjs(convertDateForDayJS(selectedNewWeek)).add("6", "day").format("YYYY-MM-DD"),
        JobId: selectedJob,
        JobTitle: jobTitle,
      });

      setSelectedJob("none");
      setSelectedNewWeek("");
      fetchAllTimesheets();

    } catch (error) {
      console.error("Error creating new timesheet:", error);
    }
  };

  const handleTimesheetSelection = async (ts) => setSelectedTimesheet(ts);

  const filteredAndSorted = timesheets.filter((ts) => {
    const isSubmitted = !!ts.SubmittedDate;
    const isApproved = !!ts.ApprovingHiringManagerId;

    return (
      (!isSubmitted && filters.notSubmitted) ||
      (isSubmitted && !isApproved && filters.submitted) ||
      (isSubmitted && isApproved && filters.approved)
    );
  }).sort((a, b) => {
    // For submission date logic
    if (sortField === "SubmittedDate") {
      const aSubmitted = !!a.SubmittedDate;
      const bSubmitted = !!b.SubmittedDate;

      // Case 1: Both unsubmitted — sort by start date
      if (!aSubmitted && !bSubmitted) {
        return dayjs(convertDateForDayJS(a.WeekStartDate)).diff(dayjs(convertDateForDayJS(b.WeekStartDate)));
      }

      // Case 2: One submitted, one not — unsubmitted first if asc
      if (aSubmitted !== bSubmitted) {
        return sortOrder === "asc"
          ? aSubmitted
            ? 1
            : -1
          : aSubmitted
            ? -1
            : 1;
      }

      // Case 3: Both submitted — sort by submission date
      return sortOrder === "asc"
        ? dayjs(convertDateForDayJS(a.SubmittedDate)).diff(dayjs(convertDateForDayJS(b.SubmittedDate)))
        : dayjs(convertDateForDayJS(b.SubmittedDate)).diff(dayjs(convertDateForDayJS(a.SubmittedDate)));
    }

    // For JobId with secondary sort by start date
    if (sortField === "JobId") {
      const aJob =
        jobs.find((j) => j.Id === a.JobId)?.Name?.toLowerCase() || "";
      const bJob =
        jobs.find((j) => j.Id === b.JobId)?.Name?.toLowerCase() || "";

      const primaryCompare =
        sortOrder === "asc"
          ? aJob.localeCompare(bJob)
          : bJob.localeCompare(aJob);

      if (primaryCompare !== 0) return primaryCompare;

      return dayjs(convertDateForDayJS(a.WeekStartDate)).diff(dayjs(convertDateForDayJS(b.WeekStartDate)));
    }

    // Generic date sorting fallback
    if (sortField.includes("date")) {
      const aDate = dayjs(convertDateForDayJS(a[sortField]));
      const bDate = dayjs(convertDateForDayJS(b[sortField]));
      return sortOrder === "asc" ? aDate.diff(bDate) : bDate.diff(aDate);
    }

    // Fallback for non-date numeric fields
    return sortOrder === "asc"
      ? a[sortField] - b[sortField]
      : b[sortField] - a[sortField];
  });

  return (
    <div className="">
      <div className="tasksContainer">
        {selectedTimesheet ? (
          <TimesheetDetails
            role={currentUser.user.Role}
            timesheet={selectedTimesheet}
            email={userEmail}
            managerId={null}
            onBack={() => {
              handleTimesheetSelection(null);
              fetchAllTimesheets();
            }}
          />
        ) : (
          <>
            {!selectedTimesheet && (
              <>
                <div className="tasksHeader">
                  {/* Dropdown to choose sort order */}
                  <div className="sortingContainer">
                    <label>Sort By:</label>
                    <Select
                      value={sortField}
                      onChange={(e) => setSortField(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <MenuItem value="WeekStartDate">Start Date</MenuItem>
                      <MenuItem value="JobId">Job</MenuItem>
                      <MenuItem value="SubmittedDate">Submission Date</MenuItem>
                    </Select>
                    <IconButton
                      onClick={() =>
                        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                      }
                    >
                      {sortOrder === "desc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )}
                    </IconButton>
                  </div>

                  {/* Create New Timesheet */}
                  <div className="newTimesheetContainer">
                    {/* Select A Job */}
                    <div className="jobSelection">
                      <Select
                        value={selectedJob}
                        onChange={(e) => handleJobSelection(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ m: 1 }}
                      >
                        {/* Map Available Jobs Pulled From Job Contracts */}
                        <MenuItem value="none" disabled>
                          Select a job
                        </MenuItem>
                        {jobs.map((job) => (
                          <MenuItem key={job.Id} value={job.Id}>
                            {job.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    <div className="calendarSelection">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Select Week"
                          value={selectedNewWeek ? dayjs(selectedNewWeek) : null}
                          onChange={(newValue) =>
                            setSelectedNewWeek(dayjs(newValue).format("YYYY-MM-DD"))
                          }
                          shouldDisableDate={(date) => {
                            const formattedDate = dayjs(date).format("YYYY-MM-DD");
                            return (
                              date.day() !== 1 ||
                              availableWeeks.length === 0 ||
                              !availableWeeks.includes(formattedDate)
                            );
                          }}
                          textField={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                            />
                          )}
                          disabled={selectedJob === "none"}
                        />
                      </LocalizationProvider>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCreateTimesheet}
                      disabled={selectedJob === "none" || !selectedNewWeek}
                      sx={{ backgroundColor: "#673ab7" }}
                    >
                      Create New Timesheet
                    </Button>
                  </div>
                </div>

                <FormGroup row sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.notSubmitted}
                        onChange={() =>
                          setFilters((prev) => ({
                            ...prev,
                            notSubmitted: !prev.notSubmitted,
                          }))
                        }
                      />
                    }
                    label="Not Submitted"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.submitted}
                        onChange={() =>
                          setFilters((prev) => ({
                            ...prev,
                            submitted: !prev.submitted,
                          }))
                        }
                      />
                    }
                    label="Submitted"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.approved}
                        onChange={() =>
                          setFilters((prev) => ({
                            ...prev,
                            approved: !prev.approved,
                          }))
                        }
                      />
                    }
                    label="Approved"
                  />
                </FormGroup>
              </>
            )}

            {loading ? (
              <CircularProgress />
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "10%" }}>
                        <strong>Start Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Job</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Hours</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell sx={{ width: "12%" }}>
                        <strong>Submission Date</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAndSorted.slice(start, limit).map((ts) => (
                      <TableRow
                        key={`${ts.WeekStartDate}-${ts.JobId}`}
                        // highlight row in red if manager rejected sheet
                        sx={{
                          backgroundColor: ts.RejectedBy
                            ? "#FFCDD2"
                            : "inherit",
                        }}
                      >
                        <TableCell>
                          {dayjs(convertDateForDayJS(ts.WeekStartDate)).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell>
                          {jobs.find((j) => j.Id === ts.JobId)?.Name ||
                            "Unknown Job"}
                        </TableCell>
                        <TableCell>{ts.HoursWorked || "0"}</TableCell>
                        <TableCell>
                          {ts.RejectedBy
                            ? `Rejected by ${ts.RejectedBy}`
                            : ts.ApprovingTgsManagerId
                              ? "Approved by TGS"
                              : ts.ApprovingHiringManagerId
                                ? "Approved by Manager"
                                : ts.SubmittedDate
                                  ? "Pending Approval"
                                  : "Pending Submission"}
                        </TableCell>

                        <TableCell>
                          {ts.SubmittedDate
                            ? dayjs(convertDateForDayJS(ts.SubmittedDate)).format("MMM D, YYYY")
                            : "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            variant="outlined"
                            onClick={() => handleTimesheetSelection(ts)}
                            sx={{ color: "#673ab7", borderColor: "#673ab7" }}
                          >
                            {ts.ApprovingHiringManagerId ? (
                              <Visibility />
                            ) : (
                              <Edit />
                            )}
                          </IconButton>

                          {!ts.SubmittedDate && (
                            <IconButton
                              variant="outlined"
                              onClick={() => {
                                setTimesheetToDelete(ts.Id);
                                setOpenDialog(true);
                              }}
                              sx={{ color: "#d32f2f", ml: 1 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  totalNumItems={filteredAndSorted.length}
                  onPageChangeNewPosition={setStart}
                  onRowsChangeNewDisplayLimit={setLimit}
                />
              </TableContainer>
            )}
          </>
        )}
      </div>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="delete-confirmation-title"
      >
        <DialogTitle id="delete-confirmation-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this timesheet? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            No
          </Button>
          <Button
            onClick={() => {
              handleDeleteTimesheet(timesheetToDelete);
              setOpenDialog(false);
            }}
            color="error"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmployeeTimesheets;
